import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Building2, ArrowLeft, Loader2, CheckCircle, XCircle,
  Wifi, WifiOff, RefreshCw, Play, AlertTriangle, Server
} from 'lucide-react';

export default function FiscalIntegracao() {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [testando, setTestando] = useState(false);
  const [processando, setProcessando] = useState(false);
  const [statusConexao, setStatusConexao] = useState(null);
  const [fila, setFila] = useState({ total: 0, itens: [] });
  
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configRes, filaRes] = await Promise.all([
        api.get('/fiscal/config'),
        api.get('/fiscal/fila', { params: { status: 'pendente' } })
      ]);
      setConfig(configRes.data);
      setFila(filaRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const testarConexao = async () => {
    setTestando(true);
    setStatusConexao(null);
    try {
      const response = await api.post('/fiscal/prefeitura/testar-conexao');
      setStatusConexao({ success: true, data: response.data });
      toast.success('Conexão estabelecida com sucesso!');
    } catch (error) {
      setStatusConexao({ success: false, error: error.response?.data?.detail || 'Erro na conexão' });
      toast.error('Falha ao conectar com a prefeitura');
    } finally {
      setTestando(false);
    }
  };

  const processarFila = async () => {
    setProcessando(true);
    try {
      const response = await api.post('/fiscal/prefeitura/processar-fila');
      toast.success(`Processados: ${response.data.processados}, Erros: ${response.data.erros}`);
      loadData(); // Recarregar dados
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar fila');
    } finally {
      setProcessando(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Skeleton className="h-[300px]" />
          <Skeleton className="h-[300px]" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="fiscal-integracao">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/fiscal')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            Integração com Prefeitura
          </h1>
          <p className="text-muted-foreground">Status de conexão e processamento de notas</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status da Conexão */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="w-5 h-5" />
              Status da Conexão
            </CardTitle>
            <CardDescription>
              Prefeitura de Taboão da Serra - SP
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {config?.configurado ? (
              <>
                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div>
                    <p className="font-medium">{config.razao_social}</p>
                    <p className="text-sm text-muted-foreground font-mono">
                      CNPJ: {config.cnpj?.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      IM: {config.inscricao_municipal}
                    </p>
                  </div>
                  <Badge variant={config.ambiente === 'producao' ? 'default' : 'secondary'}>
                    {config.ambiente === 'producao' ? 'Produção' : 'Homologação'}
                  </Badge>
                </div>

                <Button onClick={testarConexao} disabled={testando} className="w-full">
                  {testando ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Testando conexão...
                    </>
                  ) : (
                    <>
                      <Wifi className="w-4 h-4 mr-2" />
                      Testar Conexão
                    </>
                  )}
                </Button>

                {statusConexao && (
                  <div className={`p-4 rounded-lg ${statusConexao.success ? 'bg-green-50' : 'bg-red-50'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {statusConexao.success ? (
                        <>
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="font-medium text-green-700">Conexão estabelecida</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-5 h-5 text-red-600" />
                          <span className="font-medium text-red-700">Falha na conexão</span>
                        </>
                      )}
                    </div>
                    {statusConexao.success ? (
                      <p className="text-sm text-green-600">{statusConexao.data.mensagem}</p>
                    ) : (
                      <p className="text-sm text-red-600">{statusConexao.error}</p>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <WifiOff className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">
                  Configure os dados fiscais antes de testar a conexão.
                </p>
                <Button onClick={() => navigate('/fiscal/config')}>
                  Configurar Dados Fiscais
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Fila de Processamento */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5" />
              Fila de Processamento
            </CardTitle>
            <CardDescription>
              Notas aguardando emissão junto à prefeitura
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="text-3xl font-bold">{fila.total}</p>
                <p className="text-sm text-muted-foreground">notas na fila</p>
              </div>
              <Button 
                onClick={processarFila} 
                disabled={processando || fila.total === 0}
                size="lg"
              >
                {processando ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Processar Fila
                  </>
                )}
              </Button>
            </div>

            {fila.fila?.length > 0 && (
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {fila.fila.slice(0, 5).map((item, index) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">{item.nota?.tomador_nome || 'Pendente'}</p>
                      <p className="text-xs text-muted-foreground">
                        R$ {(item.nota?.valor_servicos || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-yellow-700 bg-yellow-100">
                      #{index + 1}
                    </Badge>
                  </div>
                ))}
                {fila.total > 5 && (
                  <p className="text-center text-sm text-muted-foreground pt-2">
                    + {fila.total - 5} item(ns) na fila
                  </p>
                )}
              </div>
            )}

            {fila.total === 0 && (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
                <p className="text-muted-foreground">
                  Nenhuma nota na fila de processamento.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Informações sobre integração */}
      <Card className="bg-amber-50 border-amber-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="w-5 h-5" />
            Informações Importantes
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-amber-700">
          <p className="text-sm">
            <strong>Ambiente de Homologação:</strong> As notas emitidas em homologação são 
            apenas para testes e não têm validade fiscal.
          </p>
          <p className="text-sm">
            <strong>Certificado Digital:</strong> Para emissão em produção, é necessário 
            configurar um certificado digital A1 válido.
          </p>
          <p className="text-sm">
            <strong>Processamento:</strong> O sistema processa a fila automaticamente, 
            mas você pode forçar o processamento manual clicando no botão "Processar Fila".
          </p>
          <p className="text-sm">
            <strong>Nota:</strong> A integração atual opera em modo de simulação (MOCK). 
            Para integrar com o webservice real da prefeitura, será necessário obter as 
            credenciais de acesso e certificado digital junto à Prefeitura de Taboão da Serra.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
